import { captureException } from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';

import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import posthog from '@/config/analytics/posthog';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  className?: string;
  description?: string;
  title?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

const DEFAULT_TITLE = 'Something went wrong';
const DEFAULT_DESCRIPTION =
  'We hit an unexpected error in this section. Try reloading this view or continue using other parts of the add-in.';
const DEFAULT_CLASS_NAME = 'flex h-full min-h-0 w-full items-center justify-center p-4';

/**
 * Route-level error boundary.
 *
 * Use this around route elements so a runtime error in one route subtree
 * does not take down the entire application shell. Unlike the top-level
 * RouterAwareErrorBoundary, this boundary is self-contained and offers a
 * "Try again" action that resets its own state without a full page reload.
 *
 * NOTE: Error boundaries must be class components in current React versions.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  public override state: RouteErrorBoundaryState = {
    error: null,
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error, hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('RouteErrorBoundary caught error:', error, errorInfo);
    captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    posthog.capture(ANALYTICS_EVENTS.ERROR_BOUNDARY_TRIGGERED, {
      boundary: 'route',
      error: error.message,
    });
  }

  private handleReset = () => {
    this.setState({ error: null, hasError: false });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public override render() {
    if (this.state.hasError) {
      const {
        className = DEFAULT_CLASS_NAME,
        description = DEFAULT_DESCRIPTION,
        title = DEFAULT_TITLE,
      } = this.props;

      return (
        <div className={className}>
          <div className="border-border-subtle bg-card w-full max-w-md rounded-lg border p-6 text-center shadow-md">
            <h2 className="text-secondary mb-3 text-xl font-semibold">{title}</h2>
            <p className="text-tertiary mb-5 text-sm">{description}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                className="border-border-strong text-secondary hover:bg-back-bg2 bg-card rounded-md border px-4 py-2 font-medium transition-colors"
                onClick={this.handleReset}
                type="button"
              >
                Try again
              </button>
              <button
                className="bg-primary-active text-primary-on-button hover:bg-primary-hover rounded-md px-4 py-2 font-medium transition-colors"
                onClick={this.handleReload}
                type="button"
              >
                Reload add-in
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
